import { useMutation } from '@apollo/client/react';
import { Badge, Box, Button, HStack, Input, InputGroup, Spinner, Text, VStack, chakra } from '@chakra-ui/react';
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea';
import { CommentPendingAttachments, useCommentAttachments } from '@src/components/sco/Comments/CommentAttachments';
import { CurrencyInput } from '@src/components/sco/CurrencyInput';
import { LocationInput, type LocationInputPlace } from '@src/components/sco/LocationInput';
import { useScoChatEmbedContext, type ScoQuestionAnswerPayload } from '@src/components/sco/MarkdownRenderer/ScoChatEmbedContext';
import { ScoQuestionTagsField } from '@src/components/sco/MarkdownRenderer/ScoQuestionTagsField';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { localStore } from '@src/lib/store';
import { parseAsString, useQueryState } from 'nuqs';
import { Children, isValidElement, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { LuPaperclip } from 'react-icons/lu';
export type ScoOptionProps = {
  id?: string;
  label?: string;
  'allow-free-text'?: string;
  children?: ReactNode;
};

/** Presentational option leaf — props feed parent ScoQuestion via children walk. */
export const ScoOption = (_props: ScoOptionProps) => {
  return null;
};
export type ScoQuestionProps = {
  'question-id'?: string;
  'field-type'?: string;
  selection?: string;
  ui?: string;
  prompt?: string;
  'submit-label'?: string;
  placeholder?: string;
  currency?: string;
  'tag-type-keys'?: string;
  accept?: string;
  'max-files'?: string;
  children?: ReactNode;
};
type OptionItem = {
  id: string;
  label: string;
  allowFreeText?: boolean;
};

/**
 * Known catalog keys → presentation chrome.
 * Prefer ambient `colorPalette` (inherit from chat) — do not hardcode purple/blue/etc.
 */
export type QuestionPresentation = {
  variant?: 'chips' | 'cards' | 'default';
  badge?: string;
};
export const QUESTION_PRESENTATION: Record<string, QuestionPresentation> = {
  'company-stage': {
    variant: 'chips',
    badge: 'Stage'
  },
  stage: {
    variant: 'chips',
    badge: 'Stage'
  },
  'primary-need': {
    variant: 'chips',
    badge: 'Needs'
  },
  email: {
    variant: 'default',
    badge: 'Account'
  },
  'funding-goal': {
    variant: 'default',
    badge: 'Funding'
  }
};

/**
 * Shared card chrome — colorPalette.* + sizing aligned to chat-message body
 * (MarkdownRenderer baseFontSize ~1em / 0.95em). Avoid oversized sm/default
 * controls that fight the surrounding prose.
 */
const openCardStyle = {
  borderColor: 'colorPalette.border',
  bg: 'colorPalette.subtle'
} as const;
const answeredCardStyle = {
  borderColor: 'colorPalette.border/40',
  bg: 'colorPalette.muted/25'
} as const;

/** Chip / option buttons — body-adjacent, not default Button sm. */
const chipButtonProps = {
  size: 'xs' as const,
  borderRadius: 'full' as const,
  borderWidth: '1px' as const,
  borderColor: 'colorPalette.border',
  height: 'auto' as const,
  minH: '1.75rem',
  px: 2.5,
  py: 1,
  fontSize: '0.95em',
  fontWeight: 'medium' as const,
  lineHeight: '1.3'
};
const FIELD_TYPES = new Set(['single-select', 'multi-select', 'text', 'textarea', 'currency', 'number', 'tags', 'location', 'file', 'email']);
function collectOptions(children: ReactNode): OptionItem[] {
  const options: OptionItem[] = [];
  Children.forEach(children, child => {
    if (!isValidElement(child)) {
      return;
    }
    const props = child.props as ScoOptionProps & {
      id?: string;
      label?: string;
    };
    const id = (props.id || props['id'] || '').toString().trim().toLowerCase();
    if (!id) {
      return;
    }
    const label = (props.label || props['label'] || id).toString().trim();
    const allowFreeText = props['allow-free-text'] === 'true' || id === 'other';
    options.push({
      id,
      label,
      allowFreeText
    });
  });
  return options;
}
function normalizeFieldType(props: ScoQuestionProps): string {
  let fieldType = (props['field-type'] || '').toLowerCase().trim();
  if (!fieldType && props.selection) {
    const sel = props.selection.toLowerCase();
    if (sel === 'single') {
      fieldType = 'single-select';
    } else if (sel === 'multiple' || sel === 'multi') {
      fieldType = 'multi-select';
    }
  }
  if (!fieldType) {
    fieldType = 'single-select';
  }
  if (!FIELD_TYPES.has(fieldType)) {
    fieldType = 'text';
  }
  return fieldType;
}
function promptPrefix(prompt: string) {
  return prompt.replace(/\?$/, '').trim();
}

/** Enter submits; Shift+Enter inserts newline (for textarea / multi-line). */
function onEnterSubmit(e: ReactKeyboardEvent, opts: {
  canSubmit: boolean;
  onSubmit: () => void;
  allowShiftNewline?: boolean;
}) {
  if (e.key !== 'Enter') {
    return;
  }
  if (opts.allowShiftNewline && e.shiftKey) {
    return;
  }
  // Avoid submitting while composing IME text
  if (e.nativeEvent.isComposing) {
    return;
  }
  if (!opts.canSubmit) {
    return;
  }
  e.preventDefault();
  opts.onSubmit();
}

/**
 * Tag / multi-value answers store titles in valueJson (string[]). Prefer that over
 * the joined `value` string so answered badges match combobox selections.
 */
function tagTitlesFromAnswer(answer?: {
  valueJson?: unknown;
  value?: string | number | null;
} | null): string[] | null {
  const vj = answer?.valueJson;
  if (Array.isArray(vj)) {
    const titles = vj.map(x => x == null ? '' : String(x).trim()).filter(Boolean);
    return titles.length ? titles : null;
  }
  return null;
}

/**
 * Answer-only labels for the answered card badges.
 * Never include the question/prompt — the card already shows `{prompt}` above.
 * `localSummary` / prose often look like "What stage…: Growth"; strip that prefix when present.
 */
function formatAnswerDisplay(opts: {
  answer?: {
    optionIds?: string[];
    optionLabels?: string[];
    value?: string | number | null;
    valueJson?: unknown;
    freeText?: string;
    fieldType?: string;
  } | null;
  localSummary?: string | null;
  labelsFor: (ids: string[]) => string[];
  prompt?: string;
  /** Local tag titles when embeds lag behind submit (same shape as combobox selection). */
  localTagTitles?: string[];
}): string[] {
  const {
    answer,
    localSummary,
    labelsFor,
    prompt,
    localTagTitles
  } = opts;
  if (answer?.optionLabels?.length) {
    const labels = [...answer.optionLabels];
    if (answer.freeText) {
      labels.push(answer.freeText);
    }
    return labels;
  }
  if (answer?.optionIds?.length) {
    const labels = labelsFor(answer.optionIds);
    if (answer.freeText) {
      labels.push(answer.freeText);
    }
    return labels;
  }
  const fromJson = tagTitlesFromAnswer(answer);
  if (fromJson?.length) {
    return answer?.freeText ? [...fromJson, answer.freeText] : fromJson;
  }
  if (localTagTitles?.length) {
    return localTagTitles;
  }
  if (answer?.value != null && answer.value !== '') {
    return answer.freeText ? [`${answer.value}`, answer.freeText] : [String(answer.value)];
  }
  if (answer?.freeText) {
    return [answer.freeText];
  }
  if (localSummary) {
    // Prefer splitting "A, B, C" local tag summaries into badges when possible
    const stripped = stripPromptPrefixFromSummary(localSummary, prompt);
    if (stripped.includes(', ')) {
      return stripped.split(', ').map(s => s.trim()).filter(Boolean);
    }
    return [stripped];
  }
  return ['Answered'];
}

/** Drop leading "Prompt text: " when summary was built as prose for the model. */
function stripPromptPrefixFromSummary(summary: string, prompt?: string): string {
  const trimmed = summary.trim();
  if (!trimmed) {
    return trimmed;
  }
  if (prompt) {
    const prefix = `${promptPrefix(prompt)}: `;
    if (trimmed.startsWith(prefix)) {
      return trimmed.slice(prefix.length).trim() || trimmed;
    }
  }
  return trimmed;
}

/** Short human labels for local answered UI (not the model-facing proseSummary). */
function answerOnlySummary(payload: ScoQuestionAnswerPayload, prompt?: string): string | null {
  if (payload.optionLabels?.length) {
    const labels = [...payload.optionLabels];
    if (payload.freeText) {
      labels.push(payload.freeText);
    }
    return labels.join(', ');
  }
  if (payload.value != null && payload.value !== '') {
    return payload.freeText ? `${payload.value} — ${payload.freeText}` : String(payload.value);
  }
  if (payload.freeText) {
    return payload.freeText;
  }
  const prose = (payload.proseSummary || '').trim();
  return prose ? stripPromptPrefixFromSummary(prose, prompt) : null;
}

/**
 * Interactive in-chat question card (sco-question embed).
 * Choice chips are local state only (no SelectableButtons) to avoid controlled-value loops.
 */
export const ScoQuestion = (props: ScoQuestionProps) => {
  const questionId = (props['question-id'] || '').trim().toLowerCase();
  const fieldType = normalizeFieldType(props);
  const prompt = props.prompt || 'Choose an option';
  const ui = (props.ui || '').toLowerCase() || (fieldType === 'multi-select' ? 'form' : 'buttons');
  const submitLabel = props['submit-label'] || 'Continue';
  const placeholder = props.placeholder || '';
  const currency = (props.currency || 'USD').toUpperCase();
  // Default matches bank industries question; FE normalizes `industry` → investor_industry ScoTags.
  const tagTypeKeys = (props['tag-type-keys'] || 'industry').split(',').map(s => s.trim()).filter(Boolean);
  const maxFiles = Math.max(1, parseInt(props['max-files'] || '5', 10) || 5);
  const acceptRaw = (props.accept || '').trim();
  const allowedExtensions = acceptRaw ? acceptRaw.split(',').map(s_0 => s_0.trim().replace(/^\./, '')).filter(Boolean) : ['*'];

  // Stable option list: stringify attrs so rehype re-renders don't thrash memo
  const options = useMemo(() => collectOptions(props.children), [props.children]);
  const presentation = QUESTION_PRESENTATION[questionId];
  const embedCtx = useScoChatEmbedContext();
  const state = questionId ? embedCtx.getQuestionState?.(questionId) : undefined;
  const answered = !!state?.answered;
  const answer = state?.answer;
  const progress = questionId ? embedCtx.getQuestionProgress?.(questionId) : undefined;
  const {
    currentUser,
    refetchCurrentUser
  } = useCurrentUser();
  const [, setPrompt] = useQueryState('prompt', parseAsString);
  const [, setEmailQuery] = useQueryState('email', parseAsString);
  const [registerUser] = useMutation(gql`
    mutation ScoQuestionRegisterUser($input: RegisterUserInput!) {
      registerUser(input: $input) {
        loginTokenSent
        viewer {
          id
          currentUser {
            id
            userId
            isRegistered
            isGuest
            email
          }
        }
      }
    }
  `);
  const [selected, setSelected] = useState<string[]>([]);
  const [freeText, setFreeText] = useState('');
  const [textValue, setTextValue] = useState('');
  const [numberValue, setNumberValue] = useState<string>('');
  const [currencyValue, setCurrencyValue] = useState<number>(0);
  const [tagTitles, setTagTitles] = useState<string[]>([]);
  const [locationPlace, setLocationPlace] = useState<LocationInputPlace | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [localAnswered, setLocalAnswered] = useState(false);
  const [localSummary, setLocalSummary] = useState<string | null>(null);
  const [signupHint, setSignupHint] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // File sco-questions appear on guest homepage intake; server requires explicit
  // allowUnauthenticated (same path as SignupRequestChatIntakeAskBar).
  const {
    pendingFiles,
    addFile,
    removeFile,
    isUploading,
    isFull,
    storedFileIds
  } = useCommentAttachments({
    maxFiles,
    allowedExtensions,
    bypassAuth: fieldType === 'file'
  });
  const isChoice = fieldType === 'single-select' || fieldType === 'multi-select';
  const isMulti = fieldType === 'multi-select';
  const hasOtherOption = options.some(o => o.allowFreeText);
  const otherSelected = selected.some(id => options.find(o_0 => o_0.id === id)?.allowFreeText);
  // Instant one-tap only for single-select chips with no Other option
  const isInstant = isChoice && !isMulti && ui === 'buttons' && !hasOtherOption;
  const questionReady = questionId ? embedCtx.isQuestionReady ? embedCtx.isQuestionReady(questionId) : true : false;
  const canSubmit = embedCtx.canSubmit !== false && !!embedCtx.postAnswer && questionReady;
  const showAnswered = answered || localAnswered;
  const labelsFor = useCallback((ids: string[]) => ids.map(id_0 => options.find(o_1 => o_1.id === id_0)?.label || id_0), [options]);

  /** After guest email answer: register + session login when possible; else open prefilled register. */
  const completeEmailSignup = async (email: string) => {
    if (currentUser?.isRegistered) {
      return;
    }
    const signupRequestId = localStore.get('sco:signupRequestId') || undefined;
    try {
      const res = await registerUser({
        variables: {
          input: {
            email,
            sendConfirmationRequest: true,
            signupRequestId: signupRequestId || undefined
          }
        }
      });
      const payload = res.data?.registerUser;
      if (payload?.loginTokenSent) {
        // Existing account — magic-link path. Open register surface with email so they see the flow.
        setSignupHint('We sent a login link to that email — check your inbox to continue.');
        await setEmailQuery(email);
        await setPrompt('register');
        return;
      }
      // New (or re-logged) account: session cookie set; refresh viewer + claim path via signupRequest
      await refetchCurrentUser?.();
      setSignupHint('You’re signed in — keep chatting or tap Get Started when ready.');
    } catch (err) {
      console.error('[ScoQuestion] registerUser failed, opening register form', err);
      await setEmailQuery(email);
      await setPrompt('register');
    }
  };
  const post = async (payload_0: ScoQuestionAnswerPayload) => {
    if (!embedCtx.postAnswer || submitting) {
      return;
    }
    setSubmitting(true);
    setSignupHint(null);
    try {
      await embedCtx.postAnswer(payload_0);
      setLocalAnswered(true);
      // Answer-only for badges; full proseSummary stays on the stored comment for the model
      setLocalSummary(answerOnlySummary(payload_0, prompt));
      if (payload_0.fieldType === 'email' && payload_0.value && !currentUser?.isRegistered) {
        await completeEmailSignup(String(payload_0.value));
      }
    } catch (e) {
      console.error('[ScoQuestion] postAnswer failed', e);
    } finally {
      setSubmitting(false);
    }
  };
  const toggleOption = (optionId: string) => {
    if (!canSubmit || submitting || showAnswered) {
      return;
    }
    setSelected(prev => {
      if (isMulti) {
        return prev.includes(optionId) ? prev.filter(id_1 => id_1 !== optionId) : [...prev, optionId];
      }
      // single-select: toggle or replace
      return prev.length === 1 && prev[0] === optionId ? [] : [optionId];
    });
  };
  const handleInstant = async (optionId_0: string) => {
    if (!canSubmit || showAnswered) {
      return;
    }
    const opt = options.find(o_2 => o_2.id === optionId_0);
    if (opt?.allowFreeText) {
      setSelected([optionId_0]);
      return;
    }
    const label = opt?.label || optionId_0;
    await post({
      questionId,
      fieldType,
      optionIds: [optionId_0],
      optionLabels: [label],
      proseSummary: `${promptPrefix(prompt)}: ${label}`
    });
  };
  const handleChoiceSubmit = async () => {
    if (!canSubmit || showAnswered || !selected.length) {
      return;
    }
    if (otherSelected && !freeText.trim()) {
      return;
    }
    const labels = labelsFor(selected);
    await post({
      questionId,
      fieldType,
      optionIds: selected,
      optionLabels: labels,
      freeText: otherSelected ? freeText.trim() : undefined,
      proseSummary: otherSelected ? `${promptPrefix(prompt)}: ${labels.join(', ')} — ${freeText.trim()}` : `${promptPrefix(prompt)}: ${labels.join(', ')}`
    });
  };
  const handleValueSubmit = async () => {
    if (!canSubmit || showAnswered) {
      return;
    }
    if (fieldType === 'text' || fieldType === 'textarea' || fieldType === 'email') {
      const v = textValue.trim();
      if (!v) {
        return;
      }
      if (fieldType === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        return;
      }
      await post({
        questionId,
        fieldType,
        value: v,
        proseSummary: `${promptPrefix(prompt)}: ${v}`
      });
      return;
    }
    if (fieldType === 'number') {
      const n = numberValue.trim();
      if (n === '' || Number.isNaN(Number(n))) {
        return;
      }
      await post({
        questionId,
        fieldType,
        value: Number(n),
        proseSummary: `${promptPrefix(prompt)}: ${n}`
      });
      return;
    }
    if (fieldType === 'currency') {
      if (!currencyValue || currencyValue <= 0) {
        return;
      }
      await post({
        questionId,
        fieldType,
        value: currencyValue,
        currency,
        proseSummary: `${promptPrefix(prompt)}: ${currency} ${currencyValue.toLocaleString()}`
      });
      return;
    }
    if (fieldType === 'tags') {
      if (!tagTitles.length) {
        return;
      }
      await post({
        questionId,
        fieldType,
        value: tagTitles.join(', '),
        valueJson: tagTitles,
        proseSummary: `${promptPrefix(prompt)}: ${tagTitles.join(', ')}`
      });
      return;
    }
    if (fieldType === 'location') {
      if (!locationPlace?.title) {
        return;
      }
      await post({
        questionId,
        fieldType,
        value: locationPlace.title,
        valueJson: locationPlace,
        proseSummary: `${promptPrefix(prompt)}: ${locationPlace.title}`
      });
      return;
    }
    if (fieldType === 'file') {
      if (!storedFileIds.length || isUploading) {
        return;
      }
      const names = pendingFiles.map(f => f.title).filter(Boolean);
      await post({
        questionId,
        fieldType,
        storedFileIds: [...storedFileIds],
        value: names.join(', ') || `${storedFileIds.length} file(s)`,
        proseSummary: `${promptPrefix(prompt)}: ${names.join(', ') || `${storedFileIds.length} file(s)`}`
      });
    }
  };

  // Incomplete choice markup (streaming glitch / rehype half-parse) — do not flash empty chips.
  if (isChoice && options.length < 2 && !showAnswered) {
    return null;
  }

  // No durable question id — refuse to render interactive chrome.
  if (!questionId && !showAnswered) {
    return null;
  }

  // Match chat-message density: inherit parent fontSize (1em), tight padding, md radius.
  const cardProps = {
    mt: 2,
    mb: 1,
    px: 2.5,
    py: 2,
    borderWidth: '1px' as const,
    borderRadius: 'md' as const,
    maxW: 'full' as const,
    fontSize: '1em' as const,
    lineHeight: '1.5' as const
  };
  const header = <HStack align={'start'} gap={2} justify={'space-between'} mb={1.5} wrap={'wrap'}>
      <HStack align={'center'} flex={1} gap={1.5} minW={0}>
        {presentation?.badge && <Badge flexShrink={0} fontSize={'0.75em'} size={'sm'} variant={'subtle'}>
            {presentation.badge}
          </Badge>}
        <Text color={'colorPalette.fg'} fontSize={'1em'} fontWeight={'semibold'} lineClamp={3} lineHeight={'1.5'}>
          {prompt}
        </Text>
      </HStack>
      {progress && progress.total > 1 && <Text color={'colorPalette.fg/60'} flexShrink={0} fontSize={'0.85em'} lineHeight={'1.5'}>
          {progress.index} of {progress.total}
        </Text>}
    </HStack>;
  if (showAnswered) {
    const displayLabels = formatAnswerDisplay({
      answer,
      localSummary,
      labelsFor,
      prompt,
      // Keep combobox selection as source of truth until embeds/valueJson catch up
      localTagTitles: fieldType === 'tags' && tagTitles.length ? tagTitles : undefined
    });
    return <Box {...cardProps} {...answeredCardStyle} opacity={0.95}>
        <Text color={'colorPalette.fg/70'} fontSize={'0.95em'} lineHeight={'1.5'} mb={1}>
          {prompt}
        </Text>
        <HStack gap={1.5} wrap={'wrap'}>
          {displayLabels.map((label_0, i) => <Badge key={i} size={'lg'} variant={'outline'}>
              {label_0}
            </Badge>)}
        </HStack>
        {signupHint && <Text color={'colorPalette.fg/60'} fontSize={'0.9em'} lineHeight={'1.4'} mt={1.5}>
            {signupHint}
          </Text>}
        <chakra.span display={'none'}>{props.children}</chakra.span>
      </Box>;
  }

  // ── Choice fields ──────────────────────────────────────────────
  if (isChoice) {
    // Free-text only when Other (or allow-free-text option) is currently selected — stable boolean
    const showFreeText = otherSelected;
    const freeTextRequired = otherSelected && !freeText.trim();
    const canSubmitChoice = canSubmit && !submitting && selected.length > 0 && !freeTextRequired;
    // Always show Continue for multi, form UI, or when Other is in play / selected
    const showContinue = isMulti || ui === 'form' || hasOtherOption || selected.length > 0;
    return <Box {...cardProps} {...openCardStyle}>
        {header}

        {isInstant ? <HStack gap={1.5} wrap={'wrap'}>
            {options.map(opt_0 => <Button key={opt_0.id} {...chipButtonProps} bg={'colorPalette.emphasized/20'} color={'colorPalette.fg'} disabled={!canSubmit || submitting} variant={'outline'} _hover={{
          bg: 'colorPalette.emphasized/40',
          borderColor: 'colorPalette.border'
        }} onClick={() => void handleInstant(opt_0.id)}>
                {opt_0.label}
              </Button>)}
            {submitting && <Spinner color={'colorPalette.fg'} size={'xs'} />}
          </HStack> : <VStack align={'stretch'} gap={2}>
            <HStack gap={1.5} wrap={'wrap'}>
              {options.map(opt_1 => {
            const isSel = selected.includes(opt_1.id);
            return <Button key={opt_1.id} {...chipButtonProps} aria-pressed={isSel} bg={isSel ? 'colorPalette.emphasized' : 'colorPalette.emphasized/20'} color={isSel ? 'colorPalette.contrast' : 'colorPalette.fg'} disabled={!canSubmit || submitting} variant={'outline'} _hover={{
              bg: isSel ? 'colorPalette.emphasized' : 'colorPalette.emphasized/40',
              borderColor: 'colorPalette.border'
            }} onClick={() => {
              // Instant single (non-other) only when no multi and no form-required path
              if (!isMulti && ui === 'buttons' && !opt_1.allowFreeText && !hasOtherOption) {
                void handleInstant(opt_1.id);
                return;
              }
              // Single-select non-other with Other available: select only (submit via Continue)
              // Multi and Other: toggle
              toggleOption(opt_1.id);
            }}>
                    {opt_1.label}
                  </Button>;
          })}
            </HStack>

            {showFreeText && <AutoResizeTextarea disabled={!canSubmit || submitting} fontSize={'1em'} minH={'2.25rem'} placeholder={placeholder || 'Please specify…'} size={'sm'} value={freeText} onChange={e_0 => setFreeText(e_0.target.value)} onKeyDown={e_1 => onEnterSubmit(e_1, {
          canSubmit: canSubmitChoice,
          onSubmit: () => void handleChoiceSubmit(),
          allowShiftNewline: true
        })} />}

            {showContinue && <HStack>
                <Button {...chipButtonProps} _hover={{
            bg: 'colorPalette.emphasized/90'
          }} bg={'colorPalette.emphasized'} borderColor={'colorPalette.emphasized'} borderRadius={'md'} color={'colorPalette.contrast'} disabled={!canSubmitChoice} variant={'solid'} onClick={() => void handleChoiceSubmit()}>
                  {submitting ? <Spinner size={'xs'} /> : submitLabel}
                </Button>
              </HStack>}
          </VStack>}

        <chakra.span display={'none'}>{props.children}</chakra.span>
      </Box>;
  }

  // ── Value / special fields ─────────────────────────────────────
  const valueCanSubmit = (() => {
    if (!canSubmit || submitting) {
      return false;
    }
    switch (fieldType) {
      case 'text':
      case 'textarea':
        return textValue.trim().length > 0;
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(textValue.trim());
      case 'number':
        return numberValue.trim() !== '' && !Number.isNaN(Number(numberValue));
      case 'currency':
        return !!currencyValue && currencyValue > 0;
      case 'tags':
        return tagTitles.length > 0;
      case 'location':
        return !!locationPlace?.title;
      case 'file':
        return storedFileIds.length > 0 && !isUploading;
      default:
        return textValue.trim().length > 0;
    }
  })();
  return <Box {...cardProps} {...openCardStyle}>
      {header}

      <VStack align={'stretch'} gap={2}>
        {fieldType === 'text' && <Input disabled={!canSubmit || submitting} fontSize={'1em'} placeholder={placeholder || 'Type your answer…'} size={'sm'} type={'text'} value={textValue} onChange={e_2 => setTextValue(e_2.target.value)} onKeyDown={e_3 => onEnterSubmit(e_3, {
        canSubmit: valueCanSubmit,
        onSubmit: () => void handleValueSubmit()
      })} />}

        {fieldType === 'textarea' && <AutoResizeTextarea disabled={!canSubmit || submitting} fontSize={'1em'} minH={'3.5rem'} placeholder={placeholder || 'Type your answer… (Enter to submit, Shift+Enter for newline)'} size={'sm'} value={textValue} onChange={e_4 => setTextValue(e_4.target.value)} onKeyDown={e_5 => onEnterSubmit(e_5, {
        canSubmit: valueCanSubmit,
        onSubmit: () => void handleValueSubmit(),
        allowShiftNewline: true
      })} />}

        {fieldType === 'email' && <Input autoComplete={'email'} disabled={!canSubmit || submitting} fontSize={'1em'} size={'sm'} type={'email'} value={textValue} placeholder={placeholder || (process.env.NEXT_PUBLIC_DEPLOYMENT_ENV !== 'production' ? 'you@email.com (type test++ for a unique test address)' : 'you@email.com')} onChange={e_6 => {
        const next = e_6.target.value;
        // Dev/staging shortcut — same as UserSignupRegisterForm / EmailCaptureForm
        if (process.env.NEXT_PUBLIC_DEPLOYMENT_ENV !== 'production' && next === 'test++') {
          setTextValue(`test+x${Math.round(Math.random() * 100_000_000_000)}@startups.com`);
          return;
        }
        setTextValue(next);
      }} onKeyDown={e_7 => onEnterSubmit(e_7, {
        canSubmit: valueCanSubmit,
        onSubmit: () => void handleValueSubmit()
      })} />}

        {fieldType === 'number' && <Input disabled={!canSubmit || submitting} fontSize={'1em'} placeholder={placeholder || '0'} size={'sm'} type={'number'} value={numberValue} onChange={e_8 => setNumberValue(e_8.target.value)} onKeyDown={e_9 => onEnterSubmit(e_9, {
        canSubmit: valueCanSubmit,
        onSubmit: () => void handleValueSubmit()
      })} />}

        {fieldType === 'currency' && <InputGroup startAddon={currency === 'USD' ? '$' : currency} width={'full'}>
            <Input asChild fontSize={'1em'} size={'sm'}>
              <CurrencyInput disabled={!canSubmit || submitting} fontSize={'1em'} numericValue={currencyValue || undefined} placeholder={placeholder || '500,000'} textAlign={'start'} onNumericValueChange={v_0 => setCurrencyValue(v_0 || 0)} onKeyDown={e_10 => onEnterSubmit(e_10, {
            canSubmit: valueCanSubmit,
            onSubmit: () => void handleValueSubmit()
          })} />
            </Input>
          </InputGroup>}

        {fieldType === 'tags' && <Box fontSize={'1em'} width={'full'}>
            <ScoQuestionTagsField disabled={!canSubmit || submitting} placeholder={placeholder} selectedTitles={tagTitles} tagTypeKeys={tagTypeKeys} onChange={setTagTitles} />
          </Box>}

        {fieldType === 'location' && <Box fontSize={'1em'} width={'full'}>
            <LocationInput placeholder={placeholder || 'City, state, or country'} onChange={place => {
          if (!place) {
            return;
          }
          setLocationPlace(place);
        }} />
          </Box>}

        {fieldType === 'file' && <VStack align={'stretch'} gap={1.5}>
            <HStack>
              <Button {...chipButtonProps} bg={'colorPalette.emphasized/15'} borderRadius={'md'} color={'colorPalette.fg'}
          // Upload may run as guest (bypassAuth); only Continue requires canSubmit.
          disabled={submitting || isFull || isUploading} variant={'outline'} onClick={() => fileInputRef.current?.click()}>
                <LuPaperclip />
                {isUploading ? 'Uploading…' : 'Attach file'}
              </Button>
              <input ref={fileInputRef} accept={acceptRaw || undefined} style={{
            display: 'none'
          }} type={'file'} onChange={e_11 => {
            const f_0 = e_11.target.files?.[0];
            if (f_0) {
              void addFile(f_0);
            }
            if (e_11.target) {
              e_11.target.value = '';
            }
          }} />
            </HStack>
            <CommentPendingAttachments disabled={submitting} files={pendingFiles} onRemove={removeFile} />
          </VStack>}

        <HStack>
          <Button {...chipButtonProps} _hover={{
          bg: 'colorPalette.emphasized/90'
        }} bg={'colorPalette.emphasized'} borderColor={'colorPalette.emphasized'} borderRadius={'md'} color={'colorPalette.contrast'} disabled={!valueCanSubmit} variant={'solid'} onClick={() => void handleValueSubmit()}>
            {submitting || isUploading ? <Spinner size={'xs'} /> : fieldType === 'email' && !currentUser?.isRegistered ? 'Continue with email' : submitLabel}
          </Button>
        </HStack>
      </VStack>

      <chakra.span display={'none'}>{props.children}</chakra.span>
    </Box>;
};